import { Building2, Check } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Skeleton } from "@/components/ui/skeleton"
import { ACCOUNT_NAME_MAX_LENGTH, ApiError, getAccount, renameAccount } from "@/lib/api"
import type { Account } from "@/lib/api"
import { useAuth } from "@/lib/auth"

/**
 * Settings → имя аккаунта (AMEND-8 §3 шаг 8).
 *
 * Имя рисуется в шапке консоли и в заголовке списка людей и отвечает ровно на
 * один вопрос — **в чьих данных я сейчас**. На стенде с двумя арендаторами это
 * единственное, чем один отличается от другого глазами: правила, люди и события
 * у обоих называются одинаково. Поэтому имя правится здесь, а не заводится раз и
 * навсегда миграцией: `iFinTech` из SPEC — значение по умолчанию, а не приговор.
 *
 * Меняет админ аккаунта: переименование видят сразу все его люди, и это не то
 * действие, которое стоит отдавать каждому вошедшему. Ручка отвечает 403 не
 * админу; экран целиком (вместе со списком людей) в этом случае заменяется
 * объяснением в `people.tsx`, поэтому здесь панель просто исчезает.
 *
 * Номера аккаунта в запросе нет: он приезжает из сессии, и переименовать чужой
 * отсюда нечем — ни полем, ни адресом. Разбор — в `services/admin_api/account.py`.
 */
export function AccountPanel({ onRenamed }: { onRenamed: (name: string) => void }) {
  const { accountRenamed } = useAuth()
  const [account, setAccount] = React.useState<Account | null>(null)
  const [draft, setDraft] = React.useState("")
  const [loadFailed, setLoadFailed] = React.useState<ApiError | Error | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    const controller = new AbortController()
    getAccount(controller.signal)
      .then((result) => {
        setAccount(result)
        setDraft(result.name)
        setLoadFailed(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setLoadFailed(cause instanceof Error ? cause : new Error("Request failed"))
      })
    return () => controller.abort()
  }, [])

  const trimmed = draft.trim()
  // Сохранять нечего, если поле пустое или в нём то же, что уже в базе. Второе
  // важнее первого: повторное нажатие отправило бы запрос, который ничего не
  // меняет, и ответил бы он успехом — то есть подтвердил бы правку, которой не было.
  const dirty = account !== null && trimmed !== "" && trimmed !== account.name

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!dirty) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const next = await renameAccount(trimmed)
      // Наружу показываем то, что **легло в базу** (края обрезаны сервером), а
      // не набранную строку: иначе поле и шапка разойдутся на пробел.
      setAccount(next)
      setDraft(next.name)
      setSaved(true)
      accountRenamed(next.name)
      onRenamed(next.name)
    } catch (cause: unknown) {
      setError(describe(cause))
    } finally {
      setBusy(false)
    }
  }

  // Не админ — панели нет: страница целиком объяснит, почему она закрыта.
  if (loadFailed instanceof ApiError && loadFailed.status === 403) return null

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-fg-muted" strokeWidth={1.75} />
          <span className="stencil-label text-fg-muted">Account name</span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-fg-muted">
          Shown in the header and above the people list — it is how anyone tells whose data
          this console is showing. Up to {ACCOUNT_NAME_MAX_LENGTH} characters.
        </p>
      </header>

      {loadFailed && !(loadFailed instanceof ApiError && loadFailed.status === 403) ? (
        <div className="px-4 py-3">
          <Notice tone="error">{describe(loadFailed)}</Notice>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2 px-4 py-3">
          <div className="min-w-[16rem] flex-1">
            <label htmlFor="account-name" className="stencil-label mb-1.5 block text-fg-muted">
              Name
            </label>
            {account === null ? (
              <Skeleton className="h-8 w-full" />
            ) : (
              <Input
                id="account-name"
                value={draft}
                required
                maxLength={ACCOUNT_NAME_MAX_LENGTH}
                disabled={busy}
                onChange={(event) => {
                  setDraft(event.target.value)
                  setSaved(false)
                  setError(null)
                }}
                placeholder={account.name}
              />
            )}
          </div>
          <Button type="submit" variant="primary" disabled={!dirty || busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {saved && (
            <Notice tone="ok" icon={Check} className="w-full">
              Saved. Everyone in this account sees “{account?.name}” from now on.
            </Notice>
          )}
          {error && (
            <Notice tone="error" className="w-full">
              {error}
            </Notice>
          )}
        </form>
      )}
    </section>
  )
}

/**
 * Отказ ручки человеческой строкой.
 *
 * 422 разбирается отдельно: у ошибки схемы FastAPI держит в `detail` список
 * объектов, а не строку, и клиент оставляет от него голое «HTTP 422». Показывать
 * человеку код ответа вместо того, что не так с именем, — это заставлять его
 * гадать; правило ровно одно, и оно здесь названо.
 */
function describe(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 422) {
      return `Name must be 1–${ACCOUNT_NAME_MAX_LENGTH} characters and not just spaces.`
    }
    return cause.message
  }
  return "Could not reach the API."
}
